import {ControlledSwitchOption} from '@hm/fundamental'
import {createContext, useContext, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {StaticPartComponentTypeEnum} from '@refra/modules/refra-static-parts/enums'
import {StaticPartComponentDto} from '@refra/modules/refra-static-parts/types'

interface SelectedComponentContext {
  availableComponents: StaticPartComponentTypeEnum[]
  partComponents: Omit<StaticPartComponentDto, 'sections'>[]
  setSelectedComponentType: (value: StaticPartComponentTypeEnum) => void
  selectedComponentType: StaticPartComponentTypeEnum
  selectedComponent?: Omit<StaticPartComponentDto, 'sections'>
  switchOptions: ControlledSwitchOption[]
}

const SelectedComponentContext = createContext<SelectedComponentContext | undefined>(undefined)

export const useSelectedComponent = () => {
  const context = useContext(SelectedComponentContext)
  if (!context)
    throw new Error('useSelectedComponent is used outside of the SelectedComponentProvider!')
  return context
}

interface SelectedComponentProviderProps {
  availableComponents: StaticPartComponentTypeEnum[]
  partComponents: Omit<StaticPartComponentDto, 'sections'>[]
  children: React.ReactNode
}

export const SelectedComponentProvider: React.FC<SelectedComponentProviderProps> = ({
  children,
  availableComponents,
  partComponents
}) => {
  const [selectedComponentType, setSelectedComponentType] = useState<StaticPartComponentTypeEnum>(
    availableComponents[0]
  )
  const {t} = useTranslation()

  // Handle race condition: if availableComponents loads after mount, select first component
  useEffect(() => {
    if (availableComponents.length > 0 && selectedComponentType === undefined) {
      setSelectedComponentType(availableComponents[0])
    }
  }, [availableComponents, selectedComponentType])

  const switchOptions = useMemo(() => {
    return availableComponents.map((component) => ({
      label: t(`staticParts.components.${component}`),
      value: component
    }))
  }, [availableComponents, t])

  const selectedComponent = partComponents.find(
    (component) => component.type === selectedComponentType
  )

  return (
    <SelectedComponentContext.Provider
      value={{
        selectedComponent,
        setSelectedComponentType,
        selectedComponentType,
        availableComponents,
        partComponents,
        switchOptions
      }}
    >
      {children}
    </SelectedComponentContext.Provider>
  )
}
